import { apiFetch } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  MatchingResolvePayload,
  MatchingResponse,
  MatchingV2BulkResolvePayload,
  MatchingV2BulkResolveResponse,
  MatchingV2RebindPayload,
  MatchingV2ResolvePayload,
  MatchingV2Response,
  MatchingV2UnbindPayload,
  UUID,
} from "@/api/types";

/**
 * Phase 6 — Matching.
 *
 * The endpoints below are implemented by the backend phase-6 service. The
 * frontend treats a `404` as "phase 6 not deployed yet" rather than as an
 * error: `getMatching` rethrows the typed envelope and lets the caller
 * (`MatchingTab`) render an empty-state instead.
 */

/** GET /api/meetings/:id/matching — full snapshot of bindings + candidates. */
export function getMatching(meetingId: UUID): Promise<MatchingResponse> {
  return apiFetch<MatchingResponse>(`/api/meetings/${meetingId}/matching`, {
    method: "GET",
  });
}

/** True when the error came back as 404 (phase 6 not available). */
export function isMatchingUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * POST /api/matching/{candidate_id}/resolve — bind a candidate to an entity.
 * The backend updates the speaker/client binding and (if `confirmed: true`)
 * marks it as operator-confirmed so subsequent rebuilds will not override.
 */
export function resolveMatching(
  candidateId: UUID,
  payload: MatchingResolvePayload,
): Promise<MatchingResponse> {
  return apiFetch<MatchingResponse>(`/api/matching/${candidateId}/resolve`, {
    method: "POST",
    body: payload,
  });
}

/** POST /api/matching/{candidate_id}/skip — mark candidate as deliberately unbound. */
export function skipMatching(candidateId: UUID): Promise<MatchingResponse> {
  return apiFetch<MatchingResponse>(`/api/matching/${candidateId}/skip`, {
    method: "POST",
    body: { skip: true },
  });
}

/**
 * POST /api/meetings/:id/matching/confirm-all — flip `confirmed=true` for
 * every non-pending candidate in one go. No-op on pending rows.
 */
export function confirmAllMatching(meetingId: UUID): Promise<MatchingResponse> {
  return apiFetch<MatchingResponse>(
    `/api/meetings/${meetingId}/matching/confirm-all`,
    { method: "POST" },
  );
}

/**
 * POST /api/meetings/:id/matching/rebuild — admin/manager only. Re-runs the
 * matching algorithm on the current transcript & registries. Operator-
 * confirmed bindings are preserved.
 */
export function rebuildMatching(meetingId: UUID): Promise<MatchingResponse> {
  return apiFetch<MatchingResponse>(
    `/api/meetings/${meetingId}/matching/rebuild`,
    { method: "POST" },
  );
}

// ----- Matching v2 (docs/api/matching-v2.contract.md §3) ----------------
//
// Same paths as v1 (the v2 router lives alongside the v1 one and is mounted
// only when `FEATURE_MATCHING_V2=true`). Every mutation returns the same
// `MatchingV2Response` snapshot so the FE swaps the react-query cache
// wholesale, never patches in-place. The shared query key is
// `["meeting-matching", meetingId]`.

/**
 * POST /api/meetings/:id/matching/resolve — confirm one speaker → employee
 * binding. `expected_revision` powers the CAS handshake §6: if the rebuild
 * has bumped the revision but the material state is unchanged, the BE
 * silently accepts and returns `auto_reconciled=true`.
 */
export function resolveMatchingV2(
  meetingId: UUID,
  payload: MatchingV2ResolvePayload,
): Promise<MatchingV2Response> {
  return apiFetch<MatchingV2Response>(
    `/api/meetings/${meetingId}/matching/resolve`,
    { method: "POST", body: payload },
  );
}

/**
 * POST /api/meetings/:id/matching/bulk-resolve — applies the top-1
 * suggestion to every `medium_unique` speaker in one transaction. The
 * resulting bindings land with `status='pending_confirm'` and a 10-sec
 * undo window; a sweeper promotes them to `active` automatically.
 */
export function bulkResolveMatchingV2(
  meetingId: UUID,
  payload: MatchingV2BulkResolvePayload,
): Promise<MatchingV2BulkResolveResponse> {
  return apiFetch<MatchingV2BulkResolveResponse>(
    `/api/meetings/${meetingId}/matching/bulk-resolve`,
    { method: "POST", body: payload },
  );
}

/**
 * POST /api/meetings/:id/matching/rebind-speaker — cascade-replace the
 * bound employee across every utterance of one speaker. `scope='all'`
 * is the only behaviour for v2; `'this_segment'` is reserved for v3.
 */
export function rebindSpeakerV2(
  meetingId: UUID,
  payload: MatchingV2RebindPayload,
): Promise<MatchingV2Response> {
  return apiFetch<MatchingV2Response>(
    `/api/meetings/${meetingId}/matching/rebind-speaker`,
    { method: "POST", body: payload },
  );
}

/**
 * POST /api/meetings/:id/matching/unbind — mark the active binding for
 * a speaker as `invalidated` (NOT delete — audit trail preserved). Used
 * by the undo flow on bulk-resolve.
 */
export function unbindMatchingV2(
  meetingId: UUID,
  payload: MatchingV2UnbindPayload,
): Promise<void> {
  return apiFetch<void>(`/api/meetings/${meetingId}/matching/unbind`, {
    method: "POST",
    body: payload,
  });
}
